import React from 'react';
import { Card, Typography, Space } from 'antd';
import styled from 'styled-components';

const { Title, Text } = Typography;

interface FormSidebarProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  className?: string;
  sticky?: boolean;
}

const StyledCard = styled(Card)<{ $sticky: boolean }>`
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  height: fit-content;
  position: ${props => props.$sticky ? 'sticky' : 'relative'};
  top: ${props => props.$sticky ? '24px' : 'auto'};
  
  .ant-card-head {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 20px 24px 16px;
    min-height: auto;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 16px 16px 0 0;
  }

  .ant-card-head-title {
    padding: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .ant-card-body {
    padding: 24px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    line-height: 1.3;
  }
`;

const CardSubtitle = styled(Text)`
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  line-height: 1.4;
`;

const FormActions = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const FormContent = styled.div`
  .ant-form-item:last-child {
    margin-bottom: 0;
  }
  
  .ant-input,
  .ant-select-selector,
  .ant-picker {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    
    &:hover {
      border-color: #4096ff;
    }
    
    &:focus,
    &.ant-picker-focused,
    &.ant-select-focused .ant-select-selector {
      border-color: #4096ff;
      box-shadow: 0 0 0 2px rgba(64, 150, 255, 0.1);
    }
  }
  
  /* Business buttons integration */
  .business-button {
    width: 100%;
  }
`;

export const FormSidebar: React.FC<FormSidebarProps> = ({
  title,
  subtitle,
  children,
  actions,
  loading = false,
  className = '',
  sticky = true
}) => {
  const cardTitle = (title || subtitle) ? (
    <CardHeader>
      {title && <CardTitle level={5}>{title}</CardTitle>}
      {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
    </CardHeader>
  ) : null;

  return (
    <StyledCard
      title={cardTitle}
      loading={loading}
      className={className}
      bordered={false}
      $sticky={sticky}
    >
      <FormContent>{children}</FormContent>
      {actions && <FormActions>{actions}</FormActions>}
    </StyledCard>
  );
};
